import { ButtonHTMLAttributes, ReactNode, forwardRef, useState } from 'react';

export interface MenuItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Renders the item in the destructive (`--red`) tone. Default `false`. */
  danger?: boolean;
  /** Optional leading icon, rendered before the label with a small gap. */
  icon?: ReactNode;
  children: ReactNode;
}

/**
 * A single row inside a dropdown / popover menu — full-width, left-aligned,
 * with a `--bg-tertiary` hover fill. Replaces the per-file `MenuItem` copies
 * (chat history actions, dashboard "⋯" menus) that each re-implemented the same
 * `<button>` with inline hover-mutation. `danger` switches the label to the
 * destructive tone; `icon` adds a leading glyph.
 */
export const MenuItem = forwardRef<HTMLButtonElement, MenuItemProps>(function MenuItem(
  { danger = false, icon, style, children, ...rest },
  ref,
) {
  const [hover, setHover] = useState(false);
  return (
    <button
      ref={ref}
      type="button"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        textAlign: 'left',
        padding: '6px 10px',
        border: 'none',
        background: hover ? 'var(--bg-tertiary)' : 'transparent',
        color: danger ? 'var(--red)' : 'var(--text-primary)',
        fontSize: 12,
        cursor: 'pointer',
        borderRadius: 4,
        ...style,
      }}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
});
